import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Download, Lock, Calendar, Building2, Wrench, Calculator, Activity,
  BookOpen, ExternalLink, Copy, Check, Info, AlertTriangle,
  Landmark, Sparkles, ArrowRight, Mountain, Scale, Video, Users, ListChecks,
} from "lucide-react";
import Playbooks from "./Playbooks";
import Analytics from "./Analytics";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RESOURCES } from "@/constants/testIds";
import TradeJournal from "./resources/TradeJournal";
import TradeInsights from "./resources/TradeInsights";
import InsightsTeaser from "./resources/InsightsTeaser";
import i18nInstance from "@/i18n";
import TradingViewNews from "@/components/TradingViewNews";
import Climb from "./resources/Climb";
import Grader from "./resources/Grader";
import usePromoStatus from "@/lib/usePromoStatus";

/* Partner catalogue (brokers, prop firms, software) — managed in Admin → Partners. */
function usePartners() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const [data, setData] = useState({ broker: [], prop_firm: [], software: [] });
  useEffect(() => {
    api.get("/partners", { params: { lang } }).then((r) => setData(r.data)).catch(() => {});
  }, [lang]);
  return data;
}

/* -------------------------------------------------------------------------- */
/*  Broker / Software card                                                    */
/* -------------------------------------------------------------------------- */

function OfferCard({ item, testId }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Prefer an explicit logoUrl on the item (for partners whose favicon
  // isn't served through Google's service). Fall back to Google's favicon
  // service by domain, then to the lettered initial.
  const logoUrl = item.logoUrl
    || (item.domain ? `https://www.google.com/s2/favicons?domain=${item.domain}&sz=128` : null);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(item.code);
      setCopied(true);
      toast.success(t("resourcesUi.offerCard.codeCopied", { code: item.code }));
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error(t("resourcesUi.offerCard.copyFailed"));
    }
  };

  return (
    <div
      data-testid={testId}
      className="group relative bg-white border border-slate-200 rounded-2xl p-6 flex flex-col transition-all hover:shadow-lg hover:-translate-y-1 hover:border-slate-300"
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.accent} flex items-center justify-center shrink-0 shadow-md p-2 overflow-hidden`}>
          {logoUrl && !logoFailed ? (
            <img
              src={logoUrl}
              alt={t("resourcesUi.offerCard.logoAlt", { name: item.name })}
              className="w-full h-full object-contain rounded"
              loading="lazy"
              onError={() => setLogoFailed(true)}
              // White backdrop for logos that aren't transparent-friendly
              style={{ backgroundColor: "rgba(255,255,255,0.9)" }}
            />
          ) : (
            <span className="font-display font-black text-white text-lg tracking-tight">
              {item.initial}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-lg text-slate-900 leading-tight break-words">{item.name}</h3>
          <p className="text-xs uppercase tracking-widest text-blue-800 font-semibold mt-0.5">{item.tagline}</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-600 leading-relaxed flex-1">{item.blurb}</p>

      {item.code ? (
      <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 border-dashed p-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
          {t("resourcesUi.offerCard.signupCode")}{item.perk ? ` · ${item.perk}` : ""}
        </div>
        <button
          type="button"
          onClick={copyCode}
          data-testid={`${RESOURCES.copyCodeButton}-${item.slug || item.id}`}
          className="group/code w-full flex items-center justify-between gap-2 font-mono text-xs font-bold text-slate-900 bg-white border border-slate-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg px-3 py-2 transition-colors"
          aria-label={t("resourcesUi.offerCard.copyAria", { code: item.code })}
        >
          <span className="truncate">{item.code}</span>
          <span className="text-slate-400 group-hover/code:text-blue-800 shrink-0">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </span>
        </button>
      </div>
      ) : item.perk ? (
        <div data-testid={`partner-perk-${item.slug || item.id}`} className="mt-5 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-800">
          {item.perk}
        </div>
      ) : null}

      <a
        href={item.url}
        target="_blank"
        rel="noreferrer sponsored"
        onClick={() => api.post(`/partners/${item.id}/click`).catch(() => {})}
        data-testid={`partner-visit-${item.slug || item.id}`}
        className="mt-4 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold h-10 rounded-lg transition-colors"
      >
        {t("resourcesUi.offerCard.visit", { name: item.name })} <ExternalLink size={14} />
      </a>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Position-sizing calculator                                                */
/* -------------------------------------------------------------------------- */

function CalcField({ label, value, onChange, testId, suffix, placeholder }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
        {label}
      </div>
      <div className="relative">
        <Input
          type="number"
          inputMode="decimal"
          step="any"
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-11 pr-10 bg-white border-slate-300 focus-visible:ring-blue-500"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-400">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

const PIP_PRESETS = [
  { labelKey: "resourcesUi.calculator.pipPresets.fxMajors", value: "0.0001" },
  { labelKey: "resourcesUi.calculator.pipPresets.jpyPairs",   value: "0.01" },
  { labelKey: "resourcesUi.calculator.pipPresets.goldSilver", value: "0.1" },
  { labelKey: "resourcesUi.calculator.pipPresets.indicesCfds", value: "1" },
];

function PositionCalculator() {
  const { t } = useTranslation();
  const [equity, setEquity] = useState("10000");
  const [riskMode, setRiskMode] = useState("percent"); // "percent" | "dollar"
  const [risk, setRisk] = useState("1");
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  const [pipSize, setPipSize] = useState("0.0001");

  const result = useMemo(() => {
    const e = parseFloat(equity);
    const r = parseFloat(risk);
    const en = parseFloat(entry);
    const st = parseFloat(stop);
    const tg = parseFloat(target);
    const ps = parseFloat(pipSize);
    if (!e || !r || !en || !st || !ps) return null;
    const stopDistance = Math.abs(en - st);
    if (stopDistance === 0) return null;

    // Interpret the risk input based on the toggle
    const dollarRisk = riskMode === "dollar" ? r : e * (r / 100);
    const riskPct   = riskMode === "percent" ? r : (r / e) * 100;

    const size  = dollarRisk / stopDistance;
    const pips  = stopDistance / ps;
    const rr    = tg && Math.abs(tg - en) > 0 ? Math.abs(tg - en) / stopDistance : null;

    return {
      size: size.toFixed(2),
      dollarRisk: dollarRisk.toFixed(2),
      riskPct: riskPct.toFixed(2),
      stopDistance: stopDistance.toFixed(4),
      pips: pips.toFixed(pips >= 100 ? 0 : 1),
      rr: rr ? rr.toFixed(2) : null,
      reward: rr ? (dollarRisk * rr).toFixed(2) : null,
      rewardPct: rr ? ((dollarRisk * rr) / e * 100).toFixed(2) : null,
    };
  }, [equity, riskMode, risk, entry, stop, target, pipSize]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Inputs */}
      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-blue-800 text-white flex items-center justify-center">
            <Calculator size={18} />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">{t("resourcesUi.calculator.title")}</h3>
            <p className="text-xs text-slate-500">{t("resourcesUi.calculator.formula")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <CalcField label={t("resourcesUi.calculator.accountEquity")} value={equity} onChange={setEquity} testId={RESOURCES.calcEquity} suffix="$" placeholder="10000" />

          {/* Risk with $/% toggle */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                {t("resourcesUi.calculator.riskPerTrade")}
              </div>
              <div className="flex rounded-md bg-slate-100 p-0.5">
                <button
                  type="button"
                  data-testid={RESOURCES.calcRiskModePercent}
                  onClick={() => setRiskMode("percent")}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded transition-colors ${
                    riskMode === "percent" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  %
                </button>
                <button
                  type="button"
                  data-testid={RESOURCES.calcRiskModeDollar}
                  onClick={() => setRiskMode("dollar")}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded transition-colors ${
                    riskMode === "dollar" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  $
                </button>
              </div>
            </div>
            <div className="relative">
              <Input
                type="number"
                inputMode="decimal"
                step="any"
                data-testid={RESOURCES.calcRisk}
                value={risk}
                onChange={(e) => setRisk(e.target.value)}
                placeholder={riskMode === "percent" ? "1" : "100"}
                className="h-11 pr-10 bg-white border-slate-300 focus-visible:ring-blue-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-slate-400">
                {riskMode === "percent" ? "%" : "$"}
              </span>
            </div>
          </div>

          <CalcField label={t("resourcesUi.calculator.entryPrice")} value={entry} onChange={setEntry} testId={RESOURCES.calcEntry} placeholder={t("resourcesUi.calculator.entryPlaceholder")} />
          <CalcField label={t("resourcesUi.calculator.stopPrice")}  value={stop}  onChange={setStop}  testId={RESOURCES.calcStop}  placeholder={t("resourcesUi.calculator.stopPlaceholder")} />

          <CalcField label={t("resourcesUi.calculator.targetPrice")} value={target} onChange={setTarget} testId={RESOURCES.calcTarget} placeholder={t("resourcesUi.calculator.targetPlaceholder")} />

          {/* Pip size selector */}
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
              {t("resourcesUi.calculator.pipSize")}
            </div>
            <select
              data-testid={RESOURCES.calcPipSize}
              value={pipSize}
              onChange={(e) => setPipSize(e.target.value)}
              className="h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
            >
              {PIP_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{t(p.labelKey)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-200">
          <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
          <p>{t("resourcesUi.calculator.disclaimer")}</p>
        </div>
      </div>

      {/* Verdict panel */}
      <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-slate-950 text-white p-6 border border-slate-800">
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.28em] text-amber-400 font-semibold">{t("resourcesUi.calculator.verdict")}</div>
          <div data-testid={RESOURCES.calcResult}>
            {result ? (
              <>
                <div className="mt-5">
                  <div className="text-xs text-slate-400 uppercase tracking-widest">{t("resourcesUi.calculator.positionSize")}</div>
                  <div className="font-display font-black text-4xl mt-1 text-white">
                    {result.size}
                    <span className="text-lg text-slate-400 ml-2 font-normal">{t("resourcesUi.calculator.units")}</span>
                  </div>
                </div>

                {/* Stop-distance triple */}
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                    {t("resourcesUi.calculator.stopDistance")}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div
                      data-testid={RESOURCES.calcResultPips}
                      className="bg-white/5 border border-white/10 rounded-lg px-2 py-2.5 text-center min-w-0"
                    >
                      <div className="font-mono font-bold text-white leading-none tabular-nums truncate" style={{ fontSize: "clamp(0.85rem, 1.7vw, 1.05rem)" }}>
                        {result.pips}
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-400 mt-1 font-semibold">{t("resourcesUi.calculator.pips")}</div>
                    </div>
                    <div
                      data-testid={RESOURCES.calcResultDollarRisk}
                      className="bg-white/5 border border-white/10 rounded-lg px-2 py-2.5 text-center min-w-0"
                    >
                      <div className="font-mono font-bold text-white leading-none tabular-nums truncate" style={{ fontSize: "clamp(0.85rem, 1.7vw, 1.05rem)" }}>
                        ${Number(result.dollarRisk).toLocaleString(undefined, {
                          minimumFractionDigits: Number(result.dollarRisk) >= 1000 ? 0 : 2,
                          maximumFractionDigits: Number(result.dollarRisk) >= 1000 ? 0 : 2,
                        })}
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-400 mt-1 font-semibold">{t("resourcesUi.calculator.dollarRisk")}</div>
                    </div>
                    <div
                      data-testid={RESOURCES.calcResultRiskPct}
                      className={`border border-white/10 rounded-lg px-2 py-2.5 text-center min-w-0 ${
                        parseFloat(result.riskPct) > 2 ? "bg-rose-500/10 border-rose-500/40" : "bg-white/5"
                      }`}
                    >
                      <div
                        className={`font-mono font-bold leading-none tabular-nums truncate ${
                          parseFloat(result.riskPct) > 2 ? "text-rose-300" : "text-white"
                        }`}
                        style={{ fontSize: "clamp(0.85rem, 1.7vw, 1.05rem)" }}
                      >
                        {result.riskPct}%
                      </div>
                      <div className="text-[9px] uppercase tracking-widest text-slate-400 mt-1 font-semibold">{t("resourcesUi.calculator.percentEquity")}</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2 font-mono">
                    {t("resourcesUi.calculator.rawDistance", { distance: result.stopDistance })}
                  </div>
                </div>

                {/* Reward if target set */}
                {result.rr && (
                  <div className="mt-6 pt-5 border-t border-slate-800">
                    <div className="flex items-baseline justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{t("resourcesUi.calculator.rewardRisk")}</div>
                      <div className="font-mono font-black text-2xl text-amber-400">{result.rr}R</div>
                    </div>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-slate-400">{t("resourcesUi.calculator.rewardIfHit")}</dt>
                        <dd className="font-mono font-semibold text-emerald-400">
                          ${result.reward}
                          <span className="text-slate-500 ml-1.5">({result.rewardPct}%)</span>
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </>
            ) : (
              <p className="mt-6 text-sm text-slate-400 leading-relaxed">
                {t("resourcesUi.calculator.emptyState")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Downloads (legacy /resources API — kept as its own tab)                   */
/* -------------------------------------------------------------------------- */

function Downloads() {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/resources")
      .then((r) => setResources(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500 text-sm py-6">{t("resourcesUi.downloads.loading")}</div>;
  if (!resources.length) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300">
        <Download size={28} className="mx-auto text-slate-400 mb-3" />
        <p className="text-sm text-slate-600 font-semibold">{t("resourcesUi.downloads.empty")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {resources.map((r) => (
        <div
          key={r.id}
          className={`bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-4 ${
            r.locked ? "opacity-70" : "hover:shadow-md hover:-translate-y-0.5 transition-all"
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="inline-block px-2 py-0.5 rounded bg-slate-100 uppercase tracking-widest text-[10px] font-semibold text-slate-600">
                {r.file_type || r.type}
              </div>
              <h3 className="mt-3 font-display font-bold text-lg text-slate-900">{r.title}</h3>
              <p className="mt-1 text-sm text-slate-600">{r.description}</p>
            </div>
            <div className="text-[10px] uppercase tracking-widest bg-blue-50 text-blue-800 font-semibold px-2 py-1 rounded">
              {t(`tier.${r.tier}`)}
            </div>
          </div>
          {r.locked ? (
            <Link to="/pricing" className="self-start">
              <Button variant="outline" className="border-slate-300">
                <Lock size={14} className="mr-2" />
                {t("resourcesUi.downloads.upgradeUnlock")}
              </Button>
            </Link>
          ) : (
            <a href={(() => { const u = r.file_url || r.url; return u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u; })()} target="_blank" rel="noreferrer" onClick={() => { if (r.tier && r.tier !== "free") api.post("/subscription/content-access", { area: "download", item: r.title || "" }).catch(() => {}); }} data-testid={`vault-download-${r.id}`} className="inline-flex items-center gap-2 text-blue-800 font-semibold text-sm self-start">
              <Download size={16} /> {t("resourcesUi.downloads.download")}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Economic calendar — custom render, colored impact chips, region filter    */
/* -------------------------------------------------------------------------- */

const IMPACT_STYLE = {
  low:    { labelKey: "resourcesUi.calendar.impactShort.low",    dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  medium: { labelKey: "resourcesUi.calendar.impactShort.medium",    dot: "bg-amber-500",   pill: "bg-amber-50 text-amber-700 border-amber-200" },
  high:   { labelKey: "resourcesUi.calendar.impactShort.high",   dot: "bg-rose-500",    pill: "bg-rose-50 text-rose-700 border-rose-200" },
};

function ImpactPill({ impact }) {
  const { t } = useTranslation();
  const s = IMPACT_STYLE[impact] || IMPACT_STYLE.low;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${s.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {t(s.labelKey)}
    </span>
  );
}

function formatEventTime(iso) {
  try {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString(i18nInstance.language, { weekday: "short" }),
      date: d.toLocaleDateString(i18nInstance.language, { month: "short", day: "numeric" }),
      time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    };
  } catch {
    return { day: "—", date: "—", time: "—" };
  }
}

function EconomicCalendar() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionFilter, setRegionFilter] = useState(null);
  const [impactFilter, setImpactFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOf, setWeekOf] = useState(null);



  useEffect(() => {
    Promise.all([
      api.get("/calendar/regions"),
      api.get("/calendar/events"),
    ])
      .then(([r, e]) => {
        setRegions(r.data);
        setEvents(e.data.events);
        setWeekOf(e.data.week_of);
      })
      .catch(() => toast.error(t("resourcesUi.calendar.loadError")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const nextId = useMemo(() => {
    const now = Date.now();
    return events.find((ev) => new Date(ev.datetime_utc).getTime() > now)?.id;
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((ev) => {
      if (regionFilter && ev.region !== regionFilter) return false;
      if (impactFilter && ev.impact !== impactFilter) return false;
      return true;
    });
  }, [events, regionFilter, impactFilter]);

  // Group by day for cleaner rendering
  const grouped = useMemo(() => {
    const map = new Map();
    for (const ev of filtered) {
      const ld = new Date(ev.datetime_utc);
      const key = `${ld.getFullYear()}-${String(ld.getMonth() + 1).padStart(2, "0")}-${String(ld.getDate()).padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500 text-slate-950 flex items-center justify-center">
            <Calendar size={18} />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">{t("resourcesUi.calendar.title")}</h3>
            <p className="text-xs text-slate-500">
              {weekOf ? t("resourcesUi.calendar.weekOf", { date: new Date(`${weekOf}T00:00:00`).toLocaleDateString(i18nInstance.language, { month: "long", day: "numeric", year: "numeric" }) }) : t("resourcesUi.calendar.weekLoading")}
            </p>
          </div>
        </div>
        <a
          href="https://www.myfxbook.com/forex-economic-calendar"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-800 hover:text-blue-700"
        >
          {t("resourcesUi.calendar.openLive")} <ExternalLink size={12} />
        </a>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 border-b border-slate-200 space-y-3 bg-slate-50/50">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mr-1">{t("resourcesUi.calendar.region")}</span>
          <button
            data-testid="resources-calendar-region-all"
            onClick={() => setRegionFilter(null)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              !regionFilter
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}
          >
            {t("resourcesUi.calendar.all")}
          </button>
          {regions.map((r) => (
            <button
              key={r.code}
              data-testid={`resources-calendar-region-${r.code}`}
              onClick={() => setRegionFilter(r.code)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                regionFilter === r.code
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
              }`}
              title={r.label}
            >
              <span aria-hidden>{r.flag}</span> {r.code}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mr-1">{t("resourcesUi.calendar.impact")}</span>
          {[
            { id: null,     label: "Any" },
            { id: "high",   label: "High" },
            { id: "medium", label: "Medium" },
            { id: "low",    label: "Low" },
          ].map((opt) => {
            const active = impactFilter === opt.id;
            const style = opt.id ? IMPACT_STYLE[opt.id] : null;
            return (
              <button
                key={opt.label}
                data-testid={`resources-calendar-impact-${opt.id || "any"}`}
                onClick={() => setImpactFilter(opt.id)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
              >
                {style && <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />}
                {t(opt.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div data-testid={RESOURCES.calendarFrame} className="max-h-[720px] overflow-y-auto">
        {loading ? (
          <div className="text-center py-12 text-slate-500 text-sm">{t("resourcesUi.calendar.loadingEvents")}</div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-12">
            <Calendar size={28} className="mx-auto text-slate-400 mb-3" />
            <p className="text-sm font-semibold text-slate-600">{t("resourcesUi.calendar.noEvents")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("resourcesUi.calendar.noEventsHint")}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {grouped.map(([dayKey, dayEvents]) => {
              const dayInfo = formatEventTime(dayEvents[0].datetime_utc);
              return (
                <div key={dayKey}>
                  <div className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur px-5 py-2 text-[11px] uppercase tracking-widest font-semibold text-slate-600 border-y border-slate-200 border-l-2 border-l-blue-800">
                    {dayInfo.day} · {dayInfo.date}
                  </div>
                  <div>
                    {dayEvents.map((ev) => {
                      const evTime = formatEventTime(ev.datetime_utc);
                      return (
                        <div
                          key={ev.id}
                          data-testid="resources-calendar-event"
                          className={`px-5 py-2.5 hover:bg-slate-50 transition-colors ${ev.id === nextId ? "bg-amber-50/70 border-l-2 border-amber-500" : "border-l-2 border-transparent"}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-16 shrink-0 whitespace-nowrap font-mono text-xs font-semibold text-slate-600 tabular-nums pt-0.5">{evTime.time}</div>
                            <span className="text-base leading-none pt-0.5" aria-hidden>{ev.flag}</span>
                            <div className="flex-1 min-w-0 text-sm font-semibold text-slate-900 leading-snug">{ev.event}</div>
                            <ImpactPill impact={ev.impact} />
                          </div>
                          <div className="mt-1 pl-[5.25rem] grid grid-cols-3 gap-2 text-[11px] text-slate-500">
                            <div>{t("resourcesUi.calendar.actual", "Actual")}: <span className="font-mono font-semibold text-slate-900">{ev.actual ?? "—"}</span></div>
                            <div>{t("resourcesUi.calendar.forecast")}: <span className="font-mono font-semibold text-slate-700">{ev.forecast ?? "—"}</span></div>
                            <div>{t("resourcesUi.calendar.previous")}: <span className="font-mono font-semibold text-slate-500">{ev.previous ?? "—"}</span></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 text-[11px] text-slate-500 border-t border-slate-200 bg-slate-50">
        {t("resourcesUi.calendar.footer")}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Root page                                                                 */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Funnel view — free/basic members see this instead of the tools            */
/* -------------------------------------------------------------------------- */

const FUNNEL_FEATURES = [
  { key: "climb",      Icon: Mountain   },
  { key: "grader",     Icon: Scale      },
  { key: "journal",    Icon: BookOpen   },
  { key: "calculator", Icon: Calculator },
];

function ResourcesFunnel({ tier, embedded = false }) {
  const { t } = useTranslation();
  const { params: P, loyaltyOn, referralOn } = usePromoStatus();
  const isBasic = tier === "basic";
  return (
    <div data-testid={embedded ? "resources-basic-locked" : RESOURCES.root} className="space-y-10">
      {!embedded && (
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
          {t("resourcesUi.vault.lockedEyebrow")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("vault.title")}
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {isBasic ? t("resourcesUi.vault.subtitleLocked_basic") : t("resourcesUi.vault.subtitleLocked_free")}
        </p>
      </div>
      )}

      <InsightsTeaser />

      {/* Preview grid — the four tools you're missing */}
      <div
        data-testid={RESOURCES.funnelPreview || "resources-funnel-preview"}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {FUNNEL_FEATURES.map((f) => (
          <div
            key={f.key}
            data-testid="resources-funnel-feature"
            className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow group"
          >
            <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-amber-100 group-hover:text-amber-700 transition-colors">
              <Lock size={14} />
            </div>
            <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center mb-4">
              <f.Icon size={20} />
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mb-2">
              <Sparkles size={9} /> {t(`resourcesUi.vault.features.${f.key}.tag`)}
            </span>
            <h3 className="font-display font-bold text-lg text-slate-900 tracking-tight">{t(`resourcesUi.vault.features.${f.key}.title`)}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(`resourcesUi.vault.features.${f.key}.body`)}</p>
          </div>
        ))}
      </div>

      {/* Primary CTA card — tier-specific funnel */}
      {isBasic ? (
        <div
          data-testid="resources-funnel-basic-cta"
          className="relative overflow-hidden bg-slate-950 text-white rounded-2xl border border-amber-500/25 p-10"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Path A — monthly add-on */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm flex flex-col">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-amber-400 font-semibold mb-3">
                <Sparkles size={12} /> {t("resourcesUi.vault.pathA.kicker")}
              </div>
              <h3 className="font-display font-bold text-2xl text-white tracking-tight">
                {t("resourcesUi.vault.pathA.title")}
              </h3>
              <p className="mt-2 text-sm text-slate-300 flex-1">
                {t("resourcesUi.vault.pathA.body")}
              </p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display font-black text-4xl text-amber-400 tracking-tight">$100</span>
                <span className="text-slate-400 text-sm">{t("resourcesUi.vault.pathA.priceSuffix")}</span>
              </div>
              <Link to="/shop" data-testid="resources-funnel-shop-cta" className="mt-5">
                <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-11">
                  {t("resourcesUi.vault.pathA.cta")} <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
              <Link
                to="/resources?tab=webinars"
                data-testid="resources-funnel-webinars-link"
                className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200"
              >
                {t("resourcesUi.vault.pathA.webinarsLink")} <ArrowRight size={12} />
              </Link>
            </div>

            {/* Path B — upgrade to Premium */}
            <div className="rounded-xl bg-white text-slate-900 p-6 flex flex-col">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-blue-800 font-semibold mb-3">
                <Landmark size={12} /> {t(loyaltyOn ? "resourcesUi.vault.pathB.kicker" : "resourcesUi.vault.pathB.kickerPlain")}
              </div>
              {loyaltyOn && <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 self-start mb-3">
                <Sparkles size={10} /> {t("resourcesUi.vault.pathB.loyaltyPill")}
              </div>}
              <h3 className="font-display font-bold text-2xl text-slate-900 tracking-tight">
                {t("resourcesUi.vault.pathB.title")}
              </h3>
              <p className="mt-2 text-sm text-slate-600 flex-1">
                {t("resourcesUi.vault.pathB.body")}
              </p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display font-black text-4xl text-slate-900 tracking-tight">{P.upgrade}</span>
                {loyaltyOn && <span className="text-slate-500 line-through text-sm">{P.premium}</span>}
                {loyaltyOn && <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  {t("resourcesUi.vault.pathB.saveBadge", P)}
                </span>}
              </div>
              {loyaltyOn && <div className="mt-2 text-xs text-slate-600 font-medium"
                   dangerouslySetInnerHTML={{ __html: t("resourcesUi.vault.pathB.alreadyPaid", P) }} />}

              {/* Referral discount stack — same messaging as Pricing so the
                  Basic-tier funnel matches the loyalty upgrade math. */}
              {referralOn && <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5">
                <Users size={14} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-xs font-bold text-amber-900">
                    {t(loyaltyOn ? "resourcesUi.vault.pathB.referralTitle" : "resourcesUi.vault.pathB.referralTitlePlain", P)}
                  </div>
                  <p className="mt-0.5 text-[11px] text-amber-800/90 leading-relaxed"
                     dangerouslySetInnerHTML={{ __html: t("resourcesUi.vault.pathB.referralBody", P) }} />
                </div>
              </div>}

              <Link to="/pricing" data-testid="resources-funnel-pricing-cta" className="mt-5">
                <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold h-11">
                  {t("resourcesUi.vault.pathB.cta")} <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
              <Link
                to="/account"
                data-testid="resources-funnel-referrals-link"
                className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-800 hover:text-blue-700"
              >
                {t("resourcesUi.vault.pathB.refLink")} <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div
          data-testid="resources-funnel-free-cta"
          className="relative overflow-hidden bg-slate-950 text-white rounded-2xl border border-amber-500/25 p-10"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-4">
                <Sparkles size={14} /> {t("resourcesUi.vault.freeCta.kicker")}
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
                {t("resourcesUi.vault.freeCta.title1")}<br />
                {t("resourcesUi.vault.freeCta.title2")}
              </h2>
              <p className="mt-4 text-slate-300 max-w-lg leading-relaxed text-sm">
                {t("resourcesUi.vault.freeCta.body")}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/pricing" data-testid="resources-funnel-pricing-cta">
                  <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-8">
                    {t("resourcesUi.vault.freeCta.cta1")} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/residency" data-testid="resources-funnel-residency-cta">
                  <Button size="lg" variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/10 hover:border-white h-12 px-8">
                    {t("resourcesUi.vault.freeCta.cta2")}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "tierBasic",   price: P.basic,   subKey: "tierBasicSub"   },
                { key: "tierPremium", price: P.premium, subKey: "tierPremiumSub" },
              ].map((tier) => (
                <div key={tier.key} className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-semibold">
                    {t(`resourcesUi.vault.freeCta.${tier.key}`)}
                  </div>
                  <div className="mt-1 font-display font-black text-2xl text-white tracking-tight">
                    {tier.price}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400 leading-snug">{t(`resourcesUi.vault.freeCta.${tier.subKey}`)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reassurance line — tier-aware so the copy is honest for Basic
          members (add-on required) and Free members (upgrade required). */}
      <p className="text-center text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed">
        {isBasic ? t("resourcesUi.vault.reassurance_basic") : t("resourcesUi.vault.reassurance_free")}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Resources — top-level component                                           */
/* -------------------------------------------------------------------------- */

export default function Resources() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tier = user?.tier || "free";
  const isPremium = tier === "premium" || user?.role === "admin" || !!user?.professor_addon_active;
  const [searchParams, setSearchParams] = useSearchParams();
  const isBasic = tier === "basic";
  const qTab = searchParams.get("tab") === "insights" ? "analytics" : searchParams.get("tab");
  const tab = ["calendar", "brokers", "software", "downloads", "playbooks", "calculator", "grader", "journal", "analytics", "climb"].includes(qTab)
    ? qTab
    : "calendar";
  const setTab = (id) => setSearchParams({ tab: id }, { replace: true });
  useEffect(() => {
    const open = isPremium || (isBasic && ["playbooks", "analytics"].includes(tab));
    if (open && ["playbooks", "calculator", "grader", "journal", "analytics", "climb"].includes(tab)) {
      api.post("/subscription/content-access", { area: tab }).catch(() => {});
    }
  }, [tab, isPremium, isBasic]);
  const partners = usePartners();

  // Non-premium members see a funnel screen instead of the tools. Free
  // members are pushed toward a membership; basic members are pushed
  // toward the monthly add-on OR a premium upgrade.
  if (!isPremium && !isBasic) {
    return <ResourcesFunnel tier={tier} />;
  }
  const BASIC_OPEN = ["calendar", "brokers", "software", "downloads", "playbooks", "analytics"];
  const locked = (id) => !isPremium && !BASIC_OPEN.includes(id);

  const GROUPS = [
    { id: "prepare", tabs: [
      { id: "calendar",   labelKey: "resourcesUi.tabs.calendar",   icon: Calendar,   testId: RESOURCES.tabCalendar },
      { id: "brokers",    labelKey: "resourcesUi.tabs.brokers",    icon: Building2,  testId: RESOURCES.tabBrokers },
      { id: "software",   labelKey: "resourcesUi.tabs.software",   icon: Wrench,     testId: RESOURCES.tabSoftware },
      { id: "downloads",  labelKey: "resourcesUi.tabs.downloads",  icon: Download,   testId: RESOURCES.tabDownloads },
    ] },
    { id: "plan", tabs: [
      { id: "playbooks",  labelKey: "dashboardShellUi.playbooks",  icon: ListChecks, testId: "resources-tab-playbooks" },
      { id: "calculator", labelKey: "resourcesUi.tabs.calculator", icon: Calculator, testId: RESOURCES.tabCalculator },
      { id: "grader",     labelKey: "resourcesUi.tabs.grader",     icon: Scale,      testId: RESOURCES.tabGrader },
    ] },
    { id: "execute", tabs: [
      { id: "journal",    labelKey: "resourcesUi.tabs.journal",    icon: BookOpen,   testId: RESOURCES.tabJournal },
    ] },
    { id: "review", tabs: [
      { id: "analytics",  labelKey: "dashboardShellUi.analytics",  icon: Activity,   testId: "resources-tab-analytics" },
    ] },
    { id: "grow", tabs: [
      { id: "climb",      labelKey: "resourcesUi.tabs.climb",      icon: Mountain,   testId: RESOURCES.tabClimb },
    ] },
  ];
  const goTab = (id) => setTab(id === "insights" ? "analytics" : id);

  return (
    <div data-testid={RESOURCES.root} className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
          {t("resourcesUi.vault.eyebrow")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("vault.title")}
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          {t("resourcesUi.vault.subtitlePremium")}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="h-auto w-full flex flex-wrap items-stretch justify-start gap-x-1 gap-y-2 bg-slate-100 p-1.5 rounded-xl">
          {GROUPS.map((g, gi) => (
            <div key={g.id} data-testid={`vault-group-${g.id}`} className="flex flex-col gap-1 px-1 mr-2">
              <div className="px-2 pt-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">
                <span className="text-blue-800 mr-1">{gi + 1}</span>{t(`vaultGroups.${g.id}`)}
              </div>
              <div className="flex flex-wrap gap-1">
                {g.tabs.map((it) => {
                  const Ic = it.icon;
                  return (
                    <TabsTrigger
                      key={it.id}
                      value={it.id}
                      data-testid={it.testId}
                      className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 h-9 px-3 gap-2"
                    >
                      <Ic size={14} /> {t(it.labelKey)}
                      {locked(it.id) && <Lock size={11} className="text-slate-400" />}
                    </TabsTrigger>
                  );
                })}
              </div>
            </div>
          ))}
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px] items-start">
            <EconomicCalendar />
            <aside className="xl:sticky xl:top-6">
              <TradingViewNews />
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6 space-y-10">
          <Analytics embedded />
          {isPremium ? <TradeInsights onNavigate={goTab} /> : <InsightsTeaser />}
        </TabsContent>

        <TabsContent value="playbooks" className="mt-6">
          <Playbooks embedded />
        </TabsContent>

        <TabsContent value="brokers" className="mt-6 space-y-8">
          {/* Risk reminder — neutral pill with an amber outline that traces the perimeter 3× as a gentle nudge */}
          <div
            data-testid={RESOURCES.riskWarning}
            className="warning-trace"
          >
            <div className="warning-trace-inner flex items-start gap-2 text-[11px] leading-relaxed px-3 py-2 text-slate-600">
              <AlertTriangle size={12} className="shrink-0 mt-0.5 text-amber-500" />
              <p>
                Trading CFDs, futures, and prop-firm challenges involves substantial risk of loss. Past performance is not indicative of future results. Read each firm&apos;s terms and risk disclosures carefully, and never trade capital you can&apos;t afford to lose.
              </p>
            </div>
          </div>

          {/* Affiliate disclosure */}
          <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            <Info size={13} className="shrink-0" />
            <span>Affiliate disclosure — Commissions may be earned from links on this page. Codes provide a perk for you at no extra cost.</span>
          </div>

          {/* Retail brokers */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Building2 size={16} className="text-blue-800" />
              <h2 className="font-display font-bold text-lg text-slate-900">{t("resourcesUi.retailBrokers")}</h2>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 ml-1">
                {t("resourcesUi.partnersCount", { count: partners.broker.length })}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {partners.broker.map((b) => (
                <OfferCard key={b.id} item={b} testId={`${RESOURCES.brokerCard}-${b.slug}`} />
              ))}
            </div>
          </section>

          {/* Prop firms */}
          <section data-testid={RESOURCES.propFirmsSection}>
            <div className="flex items-center gap-2 mb-4">
              <Landmark size={16} className="text-amber-600" />
              <h2 className="font-display font-bold text-lg text-slate-900">{t("resourcesUi.propFirmsTitle")}</h2>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 ml-1">
                {t("resourcesUi.propFirmsSubtitle", { count: partners.prop_firm.length })}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {partners.prop_firm.map((p) => (
                <OfferCard key={p.id} item={p} testId={`${RESOURCES.propFirmCard}-${p.slug}`} />
              ))}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="software" className="mt-6">
          <div className="mb-4 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            <Info size={13} className="shrink-0" />
            <span>Affiliate disclosure — Commissions may be earned from links on this page. Codes provide a perk for you at no extra cost.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {partners.software.map((s) => (
              <OfferCard key={s.id} item={s} testId={`${RESOURCES.softwareCard}-${s.slug}`} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="calculator" className="mt-6">
          {locked("calculator") ? <ResourcesFunnel tier="basic" embedded /> : <PositionCalculator />}
        </TabsContent>

        <TabsContent value="journal" className="mt-6">
          {locked("journal") ? <ResourcesFunnel tier="basic" embedded /> : <TradeJournal />}
        </TabsContent>

        <TabsContent value="climb" className="mt-6">
          {locked("climb") ? <ResourcesFunnel tier="basic" embedded /> : <Climb />}
        </TabsContent>

        <TabsContent value="grader" className="mt-6">
          {locked("grader") ? <ResourcesFunnel tier="basic" embedded /> : <Grader />}
        </TabsContent>

        <TabsContent value="downloads" className="mt-6">
          {locked("downloads") ? <ResourcesFunnel tier="basic" embedded /> : <Downloads />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
